import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  OnDestroy,
} from '@angular/core';
import {
  AddIconComponent,
  TickSquareIconComponent,
  CloseSquareIconComponent,
  DeleteIconComponent,
} from '@kuiper/icons';
import { Group } from '../group-model';
import { GroupService } from '../group.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Router, RouterModule } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { debounceTime, distinctUntilChanged, skip, Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  GROUP_LIST_COLUMNS_SORT,
  GROUP_LIST_VIEW_DETAILS_ACTION,
  GROUP_LIST_EDIT_ACTION,
  GROUP_LIST_DEACTIVATE_ACTION,
  GROUP_LIST_ACTIVATE_ACTION,
  GROUP_LIST_DELETE_ACTION,
  GROUP_LIST_FILTER_TABS,
} from './group-list.constants';
import { TableComponent } from '../../../../../../ui-components/lib/table/table.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { TruncatePipe } from '../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { StatusPipe } from '../../../../../../ui-components/pipes/status/status.pipe';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { SearchEnum } from '../../../../../common/enums/search.enum';
import { SearchFieldComponent } from '../../../../../../ui-components/lib/search-field/search-field.component';
import {
  initialSearchTerm,
  SearchService,
} from '../../../../../../ui-components/lib/search-field/search.service';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-group-list',
  imports: [
    NgTemplateOutlet,
    TableComponent,
    TranslocoPipe,
    BadgeComponent,
    PaginatorComponent,
    TruncatePipe,
    AvatarPipe,
    ActionListComponent,
    RouterModule,
    StatusPipe,
    AddIconComponent,
    CloseSquareIconComponent,
    TickSquareIconComponent,
    DeleteIconComponent,
    TenantPathPipe,
    SearchFieldComponent,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './group-list.component.html',
  styleUrl: './group-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GroupListComponent implements OnInit, OnDestroy {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  router = inject(Router);
  groupService = inject(GroupService);
  modalService = inject(ModalServiceService);
  transloco = inject(TranslocoService);
  toaster = inject(ToasterService);
  featureFlagsService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the panel header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );
  tenantService = inject(TenantService);
  private readonly searchService = inject(SearchService);
  subscriptions = new Subscription();
  deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  activateModal = viewChild<TemplateRef<unknown>>('activate');
  deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');

  readonly isDeleteInactiveEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .delete_inactive_groups_departments_job_titles_and_positions
    )
  ).asReadonly();

  isLoading = signal<boolean>(false);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(1);
  totalItems = signal<number>(0);
  groupList = signal<Group[]>([]);
  selectedGroup = signal<Group | undefined>(undefined);
  isGroupStateLoading = signal<boolean>(false);

  columns = signal<TableColumns[]>(
    GROUP_LIST_COLUMNS_SORT.map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );

  defaultSort = signal<{ key: string; value: 'asc' | 'desc' }>({
    key: '',
    value: 'asc',
  });
  activeSort = signal<{ key: string; value: 'asc' | 'desc' }>(
    this.defaultSort()
  );
  viewDetailsActionItem = signal<ActionList>({
    ...GROUP_LIST_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(GROUP_LIST_VIEW_DETAILS_ACTION.label),
  });
  editActionItem = signal<ActionList>({
    ...GROUP_LIST_EDIT_ACTION,
    label: this.transloco.translate(GROUP_LIST_EDIT_ACTION.label),
  });
  activeMenuItems = signal<ActionList[]>([
    this.viewDetailsActionItem(),
    this.editActionItem(),
    {
      ...GROUP_LIST_DEACTIVATE_ACTION,
      label: this.transloco.translate(GROUP_LIST_DEACTIVATE_ACTION.label),
    },
  ]);
  inactiveMenuItems = signal<ActionList[]>([
    this.viewDetailsActionItem(),
    this.editActionItem(),
    {
      ...GROUP_LIST_ACTIVATE_ACTION,
      label: this.transloco.translate(GROUP_LIST_ACTIVATE_ACTION.label),
    },
    ...(this.isDeleteInactiveEnabled()
      ? [
          {
            ...GROUP_LIST_DELETE_ACTION,
            label: this.transloco.translate(GROUP_LIST_DELETE_ACTION.label),
          },
        ]
      : []),
  ]);
  tableFilterTabs = signal<TableFilterTab[]>(
    GROUP_LIST_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  activeTableFilterTab = signal<TableFilterTab>(this.tableFilterTabs()[0]);

  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal(initialSearchTerm());
  isMoreThanThreeCharacters = signal(
    this.searchService.isMinLength(this.searchTerm())
  );
  private readonly searchTerm$ = toObservable(this.searchTerm);

  ngOnInit(): void {
    this.getGroupList();
    this.searchTermWrapper();
  }

  searchTermWrapper() {
    this.subscriptions.add(
      this.searchTerm$
        .pipe(skip(1), debounceTime(300), distinctUntilChanged())
        .subscribe(term => {
          this.handleSearchTerm(term);
        })
    );
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      this.getGroupList();
    }
  }

  getGroupList() {
    this.isLoading.set(true);
    this.totalItems.set(0);
    this.subscriptions.add(
      this.groupService
        .getGroupList(
          this.currentPage(),
          this.currentSize(),
          this.activeTableFilterTab().key,
          this.activeSort(),
          this.searchService.checkSearchLength(this.searchTerm())
        )
        .subscribe({
          next: (res: ApiResponse<any>) => {
            this.groupList.set(res.data);
            this.totalPages.set(res.meta.last_page);
            this.totalItems.set(res.meta.total);
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
          },
        })
    );
  }

  handleTableFilterTab(tab: TableFilterTab) {
    this.activeTableFilterTab.set(tab);
    this.tableFilterTabs().forEach(t => {
      t.active = t.key === tab.key;
    });
    this.currentPage.set(1);
    this.getGroupList();
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getGroupList();
  }

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set(this.defaultSort());
    }
    this.currentPage.set(1);
    this.getGroupList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getGroupList();
  }

  getStatusActionList(status: string, id: number) {
    this.viewDetailsActionItem().routerLinks = [
      this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.DETAILS}/${id}`),
    ];
    this.editActionItem().routerLinks = [
      this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.FORM}/${id}`),
    ];
    return status === 'active'
      ? this.activeMenuItems()
      : this.inactiveMenuItems();
  }

  handleAction(option: string, row: Group) {
    this.selectedGroup.set(row);
    switch (option) {
      case 'deactivate':
        this.openModal('deactivate');
        break;
      case 'activate':
        this.openModal('activate');
        break;
      case 'delete':
        this.openModal('delete');
        break;
    }
  }

  openModal(type: string) {
    let modalType;
    switch (type) {
      case 'deactivate':
        modalType = this.deactivateModal();
        break;
      case 'activate':
        modalType = this.activateModal();
        break;
      case 'delete':
        modalType = this.deleteModal();
        break;
    }
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: modalType,
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deactivateGroup() {
    this.changeGroupState({ state: 'inactive' });
  }

  activateGroup() {
    this.changeGroupState({ state: 'active' });
  }

  deleteGroup() {
    if (this.selectedGroup()?.id) {
      this.isGroupStateLoading.set(true);
      this.subscriptions.add(
        this.groupService
          .deleteGroup((<Group>this.selectedGroup()).id.toString())
          .subscribe({
            next: () => {
              this.getGroupList();
              this.closeModal();
              this.isGroupStateLoading.set(false);
            },
            error: (error: { message?: string }) => {
              this.isGroupStateLoading.set(false);
            },
          })
      );
    }
  }

  changeGroupState(body: { state: string }) {
    if (this.selectedGroup()?.id) {
      this.isGroupStateLoading.set(true);
      this.subscriptions.add(
        this.groupService
          .changeGroupState(body, (<Group>this.selectedGroup()).id.toString())
          .subscribe({
            next: () => {
              this.getGroupList();
              this.closeModal();
              this.isGroupStateLoading.set(false);
            },
            error: (error: any) => {
              this.toaster.add(error.message, '', '', 'error', true, true);
              this.isGroupStateLoading.set(false);
            },
          })
      );
    }
  }

  viewGroupDetails(group: any) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.DETAILS}/${group.id}`)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
